import { useCallback, useContext, useEffect, type RefObject, type MouseEvent, type MouseEventHandler } from 'react'
import { useLocation } from 'react-router-dom'
import useGlobalSearchURLHashVertical from './useGlobalSearchURLHashVertical'
import { SearchDrawerTimingContext } from './SearchDrawerTimingProvider'

/** Click capture also covers native button activation with Enter/Space. */
export function useSearchDrawerActivationTiming(): MouseEventHandler<HTMLElement> {
  const context = useContext(SearchDrawerTimingContext)
  const { pathname, search } = useLocation()
  const { openedSearchVertical } = useGlobalSearchURLHashVertical()
  return useCallback((event: MouseEvent<HTMLElement>) => {
    if (context && !openedSearchVertical) {
      context.openingRef.current = {
        token: context.timing.start('search_drawer', event.timeStamp),
        route: pathname + search,
      }
    }
  }, [context, openedSearchVertical, pathname, search])
}

function isVisible(element: HTMLElement) {
  return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden'
}

/** Complete only once controls, focus and opening animations are ready. */
export function useSearchDrawerReadyTiming(
  isOpen: boolean,
  vertical: string | undefined,
  drawerRef: RefObject<HTMLDivElement | null>,
  controlsRef: RefObject<HTMLDivElement | null>,
): void {
  const context = useContext(SearchDrawerTimingContext)
  useEffect(() => {
    const opening = context?.openingRef.current
    if (!context || !isOpen || !vertical || !opening) return

    let frame: number | undefined
    let observedRoot: HTMLElement = drawerRef.current ?? document.body
    const observer = new MutationObserver(scheduleCheck)

    function observeRoot() {
      observer.observe(observedRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'inert', 'hidden', 'class', 'style'],
      })
    }

    function stop() {
      if (frame !== undefined) cancelAnimationFrame(frame)
      observer.disconnect()
      document.removeEventListener('focusin', scheduleCheck)
      document.removeEventListener('transitionend', scheduleCheck, true)
      document.removeEventListener('animationend', scheduleCheck, true)
      window.removeEventListener('resize', scheduleCheck)
    }

    function scheduleCheck() {
      if (frame === undefined) frame = requestAnimationFrame(checkReady)
    }

    const checkReady = () => {
      frame = undefined
      if (context.openingRef.current !== opening) {
        stop()
        return
      }
      const drawer = drawerRef.current
      const controls = controlsRef.current
      if (drawer && drawer !== observedRoot) {
        observer.disconnect()
        observedRoot = drawer
        observeRoot()
      }
      if (drawer && !drawer.getAnimations) {
        // Without animation observation, this opening cannot be measured reliably.
        context.timing.abort(opening.token)
        context.openingRef.current = undefined
        stop()
        return
      }
      if (drawer && controls && isVisible(drawer) && drawer.contains(document.activeElement)) {
        const fields = Array.from(controls.querySelectorAll<HTMLElement>('input:not([type="hidden"]), textarea, select, button, [role="button"]')).filter(isVisible)
        const enabled = fields.some(field => !field.matches(':disabled, [aria-disabled="true"]') && !field.closest('[inert], [aria-busy="true"]'))
        // Includes delayed transitions; finished fill-mode animations do not hold readiness open.
        const animations = drawer.getAnimations({ subtree: true })
        const openingAnimationRunning = animations.some(animation =>
          animation.effect?.getComputedTiming().iterations !== Infinity &&
          (animation.playState === 'running' || animation.pending))
        if (enabled && !openingAnimationRunning) {
          stop()
          context.timing.stop(opening.token, performance.now())
          context.openingRef.current = undefined
          return
        }
        if (openingAnimationRunning) scheduleCheck()
      }
    }
    observeRoot()
    document.addEventListener('focusin', scheduleCheck)
    document.addEventListener('transitionend', scheduleCheck, true)
    document.addEventListener('animationend', scheduleCheck, true)
    window.addEventListener('resize', scheduleCheck)
    scheduleCheck()
    return stop
  }, [context, isOpen, vertical, drawerRef, controlsRef])
}
